// src/pages/Home.jsx — v8
// CHANGED FROM v7 (1 Aug 2026): passes priority={true} to the first 4
// cards in the main product grid (the real LCP candidates on this page,
// confirmed via repeated PageSpeed Insights runs) — see ProductCard.jsx
// v25. Almost Sold Out grid (further down the page, never the LCP
// element) left untouched, still lazy by default.
// CHANGED FROM v6 (31 Jul 2026): added the first-visit gender selector
// popup (Option C, locked design) — shows ONLY here on Home, only if the
// visitor has never made a choice yet (checked via the new
// 'nbs_gender_chosen' localStorage flag, kept separate from Navbar.jsx's
// 'nbs_gender' key so the two don't collide). Choosing Women/Men saves the
// gender AND navigates there; choosing "Show all" just dismisses the
// popup and stays on Home, no gender filter applied. Once dismissed
// (any choice), the flag is set and the popup never shows again on this
// device — the persistent Navbar toggle remains available afterward
// regardless. Landing directly on a product page (or any page other than
// Home) never triggers this popup, since it only lives inside Home.jsx.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import BottomNav from '../components/BottomNav';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../hooks/useProducts';
import './Home.css';
// NEW (19 Jul 2026, #3): static "About" block added below the hero —
// Google's OAuth brand-verification review rejected the app with
// "homepage doesn't clearly explain what the site does." Root cause:
// the ONLY explanatory copy on the page previously lived inside the
// auto-rotating hero carousel (advances every 3.5s), so an automated
// scan landing on a product slide would see zero description of the
// business at all. This block is static — never rotates, never
// disappears — so the site's purpose is guaranteed visible on every
// page load regardless of carousel timing.
const CATEGORIES = [
  { id: 'garments', label: 'Garments', icon: 'ti-shirt', color: 'linear-gradient(135deg, #1B2A4A 0%, #2C4070 100%)' },
  { id: 'jewellery', label: 'Jewellery', icon: 'ti-diamond', color: 'linear-gradient(135deg, #3D2B0A 0%, #7A5520 100%)' },
  { id: 'watches', label: 'Watches', icon: 'ti-clock', color: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' },
  { id: 'bags', label: 'Bags', icon: 'ti-briefcase', color: 'linear-gradient(135deg, #2d1b69 0%, #553c9a 100%)' },
  { id: 'shoes', label: 'Shoes', icon: 'ti-shoe', color: 'linear-gradient(135deg, #1a3a2a 0%, #2d6a4f 100%)' },
];
const GENDER_STORAGE_KEY = 'nbs_gender';
const GENDER_CHOSEN_FLAG = 'nbs_gender_chosen';
export default function Home() {
  const navigate = useNavigate();
  const { products, loading } = useProducts();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showGenderPopup, setShowGenderPopup] = useState(false);
  const slideProducts = products.slice(0, 8);
  const totalSlides = slideProducts.length + 1;
  const newArrivals = products.filter(p => p.tag === 'new').slice(0, 8);
  const hotSelling = products.filter(p => p.tag === 'hot').slice(0, 8);
  const almostSoldOut = products.filter(p => p.tag === 'almost' || p.tag === 'leaving').slice(0, 8);
  const displayProducts = newArrivals.length > 0 ? newArrivals : hotSelling.length > 0 ? hotSelling : products.slice(0, 8);
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 3500);
    return () => clearInterval(timer);
  }, [totalSlides, paused]);
  // NEW (v7): first-visit gender popup check — Home.jsx only, runs once.
  useEffect(() => {
    try {
      const alreadyChosen = localStorage.getItem(GENDER_CHOSEN_FLAG);
      if (!alreadyChosen) setShowGenderPopup(true);
    } catch (e) {
      // localStorage unavailable — skip popup entirely, don't block the page
    }
  }, []);
  function handleGenderPopupSelect(gender) {
    try {
      localStorage.setItem(GENDER_CHOSEN_FLAG, 'true');
      if (gender === 'women') {
        localStorage.setItem(GENDER_STORAGE_KEY, 'women');
        setShowGenderPopup(false);
        navigate('/women');
        return;
      }
      if (gender === 'men') {
        localStorage.setItem(GENDER_STORAGE_KEY, 'men');
        setShowGenderPopup(false);
        navigate('/men');
        return;
      }
      // Show all — no gender preference stored, just dismiss and stay
      localStorage.removeItem(GENDER_STORAGE_KEY);
    } catch (e) {
      // proceed without persisting
    }
    setShowGenderPopup(false);
  }
  function prevSlide() { setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides); }
  function nextSlide() { setCurrentSlide(prev => (prev + 1) % totalSlides); }
  const isBrandSlide = currentSlide === 0;
  const productSlide = isBrandSlide ? null : slideProducts[currentSlide - 1];
  function goToCategory(cat) {
    sessionStorage.setItem('lastFilter', cat);
    navigate(`/shop?cat=${cat}`);
  }
  return (
    <div className="app-shell">
      <Navbar />
      {/* First-visit gender selector popup */}
      {showGenderPopup && (
        <div className="gender-popup-overlay">
          <div className="gender-popup">
            <div className="gender-popup-title">Shop for</div>
            <p className="gender-popup-sub">Pick one — you can always change this later from the top menu.</p>
            <div className="gender-popup-btns">
              <button className="gender-popup-btn women" onClick={() => handleGenderPopupSelect('women')}>Women</button>
              <button className="gender-popup-btn men" onClick={() => handleGenderPopupSelect('men')}>Men</button>
              <button className="gender-popup-btn all" onClick={() => handleGenderPopupSelect('all')}>Show all</button>
            </div>
          </div>
        </div>
      )}
      {/* Mobile Hero */}
      <div className="mobile-hero" onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}>
        {!isBrandSlide && productSlide?.imageUrl && (
          <div className="mobile-hero-bg" style={{ backgroundImage: `url(${productSlide.imageUrl})` }} />
        )}
        <div className="mobile-hero-overlay" />
        {isBrandSlide ? (
          <div className="mobile-hero-brand">
            <h1 className="mobile-hero-title">Style without<br />the <span className="hero-highlight">label.</span></h1>
            <p className="mobile-hero-sub">dressed. not branded.</p>
            <button className="mobile-hero-btn" onClick={() => navigate('/shop')}>Shop now</button>
          </div>
        ) : (
          <div className="mobile-hero-product">
            <div className="mobile-slide-info">
              <div className="mobile-slide-name">{productSlide?.name}</div>
              <div className="mobile-slide-price">₹{productSlide?.price?.toLocaleString()}</div>
            </div>
            <button className="mobile-slide-buy" onClick={() => navigate(`/product/${productSlide?.id}`)}>Buy now →</button>
          </div>
        )}
        <button className="hero-arrow hero-arrow-left" onClick={prevSlide}>&#8249;</button>
        <button className="hero-arrow hero-arrow-right" onClick={nextSlide}>&#8250;</button>
        <div className="hero-dots">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button key={i} className={`hero-dot ${i === currentSlide ? 'active' : ''}`} onClick={() => setCurrentSlide(i)} />
          ))}
        </div>
      </div>
      {/* Desktop Hero */}
      <div className="home-hero desktop-only">
        <div className="hero-left">
          <h1 className="hero-title">Style without<br />the <span className="hero-highlight">label.</span></h1>
          <p className="hero-sub">dressed. not branded.</p>
          <div className="hero-actions">
            <button className="btn-blue hero-shop-btn" onClick={() => navigate('/shop')}>Shop now</button>
          </div>
        </div>
        <div className="hero-slideshow">
          {!loading && productSlide && (
            <>
              <div className="slide-img-wrap">
                {productSlide.imageUrl ? (
                  <img src={productSlide.imageUrl} alt={productSlide.name} className="slide-img" />
                ) : (
                  <div className="slide-img-placeholder"><i className="ti ti-shirt"></i></div>
                )}
                <div className="slide-overlay">
                  <div className="slide-name">{productSlide.name}</div>
                  <div className="slide-price">₹{productSlide.price?.toLocaleString()}</div>
                  <button className="slide-btn" onClick={() => navigate(`/product/${productSlide.id}`)}>Shop now →</button>
                </div>
              </div>
              <div className="slide-dots">
                {slideProducts.map((_, i) => (
                  <button key={i} className={`slide-dot ${i === currentSlide - 1 ? 'active' : ''}`} onClick={() => setCurrentSlide(i + 1)} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {/* Categories */}
      <div className="page-body" style={{ paddingBottom: 0 }}>
        <div className="section-header">
          <span className="section-title">Shop by category</span>
        </div>
        <div className="cat-scroll">
          {CATEGORIES.map(cat => (
            <div key={cat.id} className="cat-scroll-card" onClick={() => goToCategory(cat.id)} style={{ background: cat.color }}>
              <i className={`ti ${cat.icon} cat-scroll-icon`}></i>
              <div className="cat-scroll-label">
                <div className="cat-scroll-name">{cat.label}</div>
                <div className="cat-scroll-count">{products.filter(p => p.category === cat.label).length}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* New Arrivals / Hot Selling */}
      <div className="page-body" style={{ paddingTop: 20 }}>
        <div className="section-header">
          <span className="section-title">
            {newArrivals.length > 0 ? '✦ New Arrivals' : hotSelling.length > 0 ? '🔥 Hot Selling' : 'Latest Products'}
          </span>
          <button className="view-all-pill" onClick={() => navigate('/shop')}>View all <i className="ti ti-arrow-right"></i></button>
        </div>
        {loading ? (
          <div className="loading-grid">
            {[...Array(4)].map((_, i) => <div key={i} className="prod-skeleton"></div>)}
          </div>
        ) : (
          <div className="prod-grid">
            {displayProducts.map((p, i) => <ProductCard key={p.id} product={p} priority={i < 4} />)}
          </div>
        )}
      </div>
      {/* Almost Sold Out */}
      {almostSoldOut.length > 0 && (
        <div className="page-body" style={{ paddingTop: 0 }}>
          <div className="section-header">
            <span className="section-title" style={{ color: '#E24B4A' }}>⚠ Almost Sold Out</span>
            <button className="view-all-pill" onClick={() => navigate('/shop')}>View all <i className="ti ti-arrow-right"></i></button>
          </div>
          <div className="prod-grid">
            {almostSoldOut.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
      <Footer />
      <BottomNav />
      <style>{`
        .view-all-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: var(--navy, #1B2A4A);
          color: #fff;
          border: none;
          border-radius: 999px;
          padding: 6px 14px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          font-family: var(--font);
        }
        .view-all-pill i {
          font-size: 13px;
        }
        .gender-popup-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.6);
          z-index: 300;
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }
        .gender-popup {
          background: #fff;
          border-radius: 16px 16px 0 0;
          width: 100%;
          max-width: 430px;
          padding: 24px 20px 28px;
          text-align: center;
        }
        .gender-popup-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--navy);
          margin-bottom: 6px;
        }
        .gender-popup-sub {
          font-size: 12px;
          color: var(--muted);
          margin-bottom: 20px;
        }
        .gender-popup-btns {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .gender-popup-btn {
          padding: 14px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: var(--font);
          border: none;
        }
        .gender-popup-btn.women, .gender-popup-btn.men {
          background: var(--navy);
          color: #fff;
        }
        .gender-popup-btn.all {
          background: transparent;
          color: var(--navy);
          border: 0.5px solid var(--border-strong, rgba(27,42,74,0.2));
        }
      `}</style>
    </div>
  );
}